import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronDown, CloudUpload, RotateCcw } from 'lucide-react';
import { formatUploadBytes, formatUploadEta, truncateFileName } from '../lib/uploadProgressFormat';

function overallStats(jobs) {
  const visible = jobs.filter((job) => job.status !== 'cancelled');
  const active = visible.filter((job) => !['complete', 'cancelled'].includes(job.status));
  const bytesUploaded = visible.reduce((sum, job) => sum + (job.bytesUploaded || 0), 0);
  const bytesTotal = visible.reduce((sum, job) => sum + (job.bytesTotal || 0), 0);
  const fileCount = visible.reduce((sum, job) => sum + (job.files?.length || 0), 0);
  const compressing = active.some((job) => (
    job.status === 'compressing' || job.files?.some((file) => file.status === 'compressing')
  ));
  const compressPercent = compressing
    ? Math.round(
        active.reduce((sum, job) => (
          sum + (job.files || []).reduce((fileSum, file) => fileSum + (file.compressPercent || 0), 0)
        ), 0) / Math.max(1, active.reduce((sum, job) => sum + (job.files?.length || 0), 0))
      )
    : 0;
  const percent = compressing
    ? Math.min(29, Math.round(compressPercent * 0.3))
    : (bytesTotal ? Math.min(100, Math.round((bytesUploaded / bytesTotal) * 100)) : 0);
  const etaSeconds = active
    .map((job) => job.etaSeconds)
    .filter((n) => n != null)
    .reduce((max, n) => Math.max(max, n), 0);
  const hasError = visible.some((job) => job.status === 'error');
  const allComplete = visible.length > 0 && visible.every((job) => job.status === 'complete');
  return {
    visible,
    active,
    bytesUploaded,
    bytesTotal,
    fileCount,
    percent,
    etaSeconds: etaSeconds || null,
    hasError,
    allComplete,
    compressing,
  };
}

function titleFor(stats) {
  if (stats.allComplete) {
    const stitchJob = stats.visible.some((job) => job.mode === 'stitch' || job.stitchPending);
    if (stitchJob) {
      return stats.fileCount === 1 ? 'Clip uploaded — joining video' : 'Clips uploaded — joining into one video';
    }
    return stats.fileCount === 1 ? '1 video uploaded' : `${stats.fileCount} videos uploaded`;
  }
  if (stats.hasError) return 'Upload interrupted';
  if (stats.compressing) {
    return stats.fileCount === 1 ? 'Compressing video' : `Compressing ${stats.fileCount} videos`;
  }
  return stats.fileCount === 1 ? 'Uploading 1 video' : `Uploading ${stats.fileCount} videos`;
}

const UploadProgressBubble = ({ jobs = [], onRetry, onCancel, onDismiss }) => {
  const stats = useMemo(() => overallStats(jobs), [jobs]);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (stats.hasError) setExpanded(true);
  }, [stats.hasError]);

  if (stats.visible.length === 0) return null;

  const etaLabel = formatUploadEta(stats.etaSeconds);
  const progressLabel = stats.bytesTotal
    ? `${formatUploadBytes(stats.bytesUploaded)} / ${formatUploadBytes(stats.bytesTotal)}${etaLabel ? ` · ${etaLabel}` : ''}`
    : '';

  return (
    <div className="fixed bottom-4 right-4 z-[80] w-[min(100%-1.5rem,380px)] font-sans">
      <div
        className="rounded-2xl border border-stone-200/80 bg-[#eef1ea] shadow-[0_12px_40px_rgba(28,35,28,0.18)] overflow-hidden"
        role="status"
        aria-live="polite"
      >
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="w-full flex items-center gap-3 px-4 pt-3.5 pb-3 text-left min-h-[44px]"
        >
          <span className={`flex h-10 w-10 items-center justify-center rounded-xl shrink-0 ${
            stats.allComplete ? 'bg-emerald-100 text-emerald-700' : stats.hasError ? 'bg-amber-100 text-amber-800' : 'bg-white/80 text-slate-700'
          }`}>
            {stats.allComplete ? <CheckCircle2 className="h-5 w-5" /> : <CloudUpload className="h-5 w-5" />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-slate-900 leading-tight">{titleFor(stats)}</span>
            <span className="block text-xs text-slate-500 mt-0.5 truncate">
              {stats.active[0]?.locationLabel || stats.visible[0]?.locationLabel || 'Match library'}
            </span>
          </span>
          <ChevronDown className={`h-4 w-4 text-slate-500 shrink-0 transition-transform ${expanded ? 'rotate-0' : '-rotate-90'}`} />
        </button>

        <div className="px-4">
          <div className="h-[3px] w-full rounded-full bg-stone-300/70 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                stats.hasError ? 'bg-amber-500' : stats.allComplete ? 'bg-emerald-500' : 'bg-slate-700'
              }`}
              style={{ width: `${stats.allComplete ? 100 : Math.max(stats.percent, stats.bytesUploaded > 0 ? 2 : 0)}%` }}
            />
          </div>
        </div>

        {expanded ? (
          <div className="px-3 pb-3 pt-3 space-y-2">
            {stats.visible.map((job) => (
              <div key={job.id} className="space-y-2">
                {job.files.map((file) => (
                  <div key={file.id} className="flex items-center gap-3 rounded-xl bg-white/70 px-2.5 py-2">
                    <div className="h-11 w-[72px] rounded-lg overflow-hidden bg-slate-200 shrink-0">
                      {file.thumbnailUrl ? (
                        <img src={file.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-slate-300 to-slate-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate" title={file.name}>
                        {truncateFileName(file.name)}
                      </p>
                      <p className="text-xs text-slate-500 tabular-nums mt-0.5">
                        {job.status === 'complete'
                          ? `${formatUploadBytes(file.size)} · Done`
                          : job.status === 'error'
                            ? (job.error || 'Upload failed')
                            : job.status === 'completing'
                              ? 'Saving to match…'
                              : file.status === 'compressing' || job.status === 'compressing'
                                ? `Compressing… ${Math.max(file.compressPercent || 0, 0)}%`
                                : job.status === 'queued'
                                  ? (job.error || 'Starting upload…')
                                  : `${formatUploadBytes(file.loaded || 0)} / ${formatUploadBytes(file.size)}${
                                    job.etaSeconds ? ` · ${formatUploadEta(job.etaSeconds)}` : ''
                                  }`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {job.href && (job.status === 'complete' || job.status === 'uploading' || job.status === 'compressing' || job.status === 'completing' || job.status === 'queued') ? (
                        <Link
                          to={job.href}
                          className="rounded-full bg-stone-200/90 px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-stone-300 min-h-[32px] inline-flex items-center"
                        >
                          View
                        </Link>
                      ) : null}
                      {job.status !== 'complete' ? (
                        <button
                          type="button"
                          onClick={() => onCancel?.(job.id)}
                          className="rounded-full px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-stone-200/90 hover:text-slate-900 min-h-[32px] inline-flex items-center"
                        >
                          Cancel
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}

                {job.status === 'error' ? (
                  <div className="flex items-center justify-end gap-2 px-1">
                    <button
                      type="button"
                      onClick={() => onRetry?.(job.id)}
                      className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 min-h-[32px]"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Resume
                    </button>
                  </div>
                ) : null}

                {job.status === 'complete' ? (
                  <div className="flex justify-end px-1">
                    <button
                      type="button"
                      onClick={() => onDismiss?.(job.id)}
                      className="text-xs font-medium text-slate-500 hover:text-slate-800 px-2 py-1"
                    >
                      Dismiss
                    </button>
                  </div>
                ) : null}
              </div>
            ))}

            {!stats.allComplete ? (
              <p className="text-[11px] text-slate-500 px-1 pt-1">
                You can keep using Statbox. Leave this tab open until compression and upload finish.
              </p>
            ) : null}
          </div>
        ) : (
          <p className="px-4 pb-3 pt-2 text-[11px] text-slate-500 tabular-nums">
            {stats.allComplete ? 'Finished' : progressLabel || `${stats.percent}%`}
          </p>
        )}
      </div>
    </div>
  );
};

export default UploadProgressBubble;
